import { useImperativeHandle, useState, forwardRef, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    Typography,
    styled,
    ListItemButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../redux/reduxHooks";
import { UserStructureResponse } from "../../structures/model/StructureModel";
import { RootState } from "../redux/store";
import { Box } from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { ArrowForward, HourglassEmpty } from "@mui/icons-material";
import {
    fetchCheckoutLink,
    fetchPortalLink,
    fetchSubscriptionPlans,
    SubscriptionPlan,
} from "../redux/subscriptionActions";

export type SubscriptionModalHandle = {
    open: () => void;
    close: () => void;
};

type SubscriptionModalProps = {
    context?: "project" | "availability";
};

const SubscriptionModal = forwardRef<
    SubscriptionModalHandle,
    SubscriptionModalProps
>(({ context = "project" }, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [subscriptionPlans, setSubscriptionPlans] = useState<
        SubscriptionPlan[]
    >([]);
    const [plansLoading, setPlansLoading] = useState(false);

    const userSelector: UserStructureResponse = useAppSelector(
        (state: RootState) => state.user.data
    );

    const mode = userSelector.subscription?.active ? "upgrade" : "inital";

    useImperativeHandle(ref, () => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
    }));

    const getSubscriptionPlans = async () => {
        try {
            setPlansLoading(true);
            const plans = await fetchSubscriptionPlans();
            setSubscriptionPlans(plans);
        } catch (error) {
        } finally {
            setPlansLoading(false);
        }
    };

    const checkout = async (planId: string) => {
        try {
            const link = await fetchCheckoutLink(planId);
            if (link) {
                window.location.href = link;
            }
        } catch (error) {
            console.error("Failed to redirect to checkout:", error);
        }
    };

    const openSubscriptionPortal = async () => {
        try {
            const link = await fetchPortalLink();
            if (link) window.location.href = link;
        } catch (error) {
            console.error("Error fetching subscription portal link:", error);
        }
    };

    useEffect(() => {
        if (open) {
            console.log("User subscription:", userSelector.subscription);
            getSubscriptionPlans();
        }
    }, [open]);

    return (
        <StyledDialog open={open} onClose={() => setOpen(false)}>
            <Box display="flex" justifyContent="center" mt={3} mb={1}>
                <WorkspacePremiumIcon sx={{ fontSize: 48, color: "#e53835" }} />
            </Box>
            <DialogTitle sx={{ textAlign: "center", color: "#e53835" }}>
                {t(`subscriptionModal.${mode}.${context}.title`)}
            </DialogTitle>
            <DialogContent>
                <Typography
                    sx={{ textAlign: "center", color: "#333" }}
                    variant="body1"
                    gutterBottom
                >
                    {t(`subscriptionModal.${mode}.${context}.choosePlan`)}
                </Typography>
                {context === "availability" && (
                    <Typography
                        sx={{
                            textAlign: "center",
                            color: "#777",
                            mt: 1,
                            mb: 2,
                        }}
                        variant="body2"
                    >
                        {t("subscriptionModal.sendAvailabilityNotice")}
                    </Typography>
                )}
                <List>
                    {plansLoading && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            py={2}
                        >
                            <HourglassEmpty
                                sx={{
                                    fontSize: 32,
                                    color: "#e53835",
                                    animation: "spin 1s linear infinite",
                                    "@keyframes spin": {
                                        "0%": { transform: "rotate(0deg)" },
                                        "100%": { transform: "rotate(360deg)" },
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {!plansLoading &&
                        mode === "inital" &&
                        subscriptionPlans.map((plan) => (
                            <ListItem key={plan.id} divider>
                                <ListItemButton
                                    onClick={() =>
                                        !plan.internal && checkout(plan.id)
                                    }
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{ color: "#e53835", flexGrow: 1 }}
                                    >
                                        {plan.name}
                                    </Typography>
                                    <div>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#333",
                                                fontWeight: 400,
                                            }}
                                        >
                                            {typeof plan.monthlyPrice ===
                                            "number"
                                                ? t(
                                                      "subscriptionModal.monthlyPrice",
                                                      {
                                                          price: plan.monthlyPrice.toFixed(
                                                              2
                                                          ),
                                                      }
                                                  )
                                                : t(
                                                      "subscriptionModal.priceOnRequest"
                                                  )}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: "#333",
                                                fontWeight: 400,
                                            }}
                                            variant="caption"
                                        >
                                            {plan.projectSlots >= 1000
                                                ? t(
                                                      "subscriptionModal.unlimitedProjects"
                                                  )
                                                : t(
                                                      "subscriptionModal.projectSlots",
                                                      {
                                                          count: plan.projectSlots,
                                                      }
                                                  )}
                                        </Typography>
                                    </div>
                                    {!plan.internal && (
                                        <ArrowForward
                                            sx={{
                                                marginLeft: 1,
                                                color: "#333",
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        ))}
                    {!plansLoading && mode === "upgrade" && (
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onClick={openSubscriptionPortal}
                        >
                            {t("profileForm.subscription.manageSubscription")}
                        </Button>
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    {t("subscriptionModal.close")}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
});

export default SubscriptionModal;

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        min-width: 400px;
    }
`;
